import { IItemVersion, ItemDetails } from "lib/types/items";

/**
 * Parent-Child Product Helper Utilities
 *
 * This module provides utility functions for identifying and working with parent-child products.
 * Parent-child products are products that have multiple team versions (like Canva teams or
 * Confluence teams) sharing the same product instance but requiring separate access requests.
 */

/**
 * Determines if a product is a Parent-Child product by checking if all versions
 * share the same applinkInstanceName. This indicates that the versions represent
 * different teams/groups within the same product instance (like Canva teams or Confluence teams)
 * rather than different product instances (like Qualtrics regions).
 *
 * @param item - The item details object containing product information
 * @param versions - Array of item versions to analyze for parent-child relationship
 * @returns True if the product is identified as a parent-child product, false otherwise
 *
 * @example
 * ```typescript
 * const isParentChild = isParentChildProduct(itemDetails, itemVersions);
 * if (isParentChild) {
 *   // Show team access request functionality
 * }
 * ```
 */
export const isParentChildProduct = (item: ItemDetails, versions: IItemVersion[]): boolean => {
  // Need at least 2 versions to be considered Parent-Child
  if (!versions || versions.length <= 1) {
    return false;
  }

  const firstInstanceName = versions[0]?.applinkInstanceName;
  if (!firstInstanceName) {
    return false;
  }

  // Check if ALL versions share the same applinkInstanceName
  // Filter out versions without applinkInstanceName to avoid false negatives
  const versionsWithInstanceName = versions.filter((version) => version.applinkInstanceName);

  return (
    versionsWithInstanceName.length > 1 &&
    versionsWithInstanceName.every((version) => version.applinkInstanceName === firstInstanceName)
  );
};

/**
 * Gets the available teams/versions for a Parent-Child product
 * Transforms IItemVersion array into a simplified format suitable for team selection UI
 *
 * @param versions - Array of item versions representing different teams
 * @returns Array of simplified team objects with taxonomyId and displayText
 *
 * @example
 * ```typescript
 * const availableTeams = getAvailableTeams(itemVersions);
 * // Returns: [{ taxonomyId: "team1", displayText: "Marketing Team" }, ...]
 * ```
 */
export const getAvailableTeams = (
  versions: IItemVersion[]
): Array<{
  taxonomyId: string;
  displayText: string;
}> => {
  return versions.map((version) => ({
    taxonomyId: version.taxonomyId,
    displayText: version.name || version.displayText || version.taxonomyId,
  }));
};

/**
 * Validates if a product has the necessary data structure for parent-child functionality
 * Checks for required fields and data integrity
 *
 * @param item - The item details object to validate
 * @param versions - Array of item versions to validate
 * @returns True if the product has valid parent-child data structure, false otherwise
 *
 * @example
 * ```typescript
 * const isValid = validateParentChildData(itemDetails, itemVersions);
 * if (isValid && isParentChildProduct(itemDetails, itemVersions)) {
 *   // Safe to use parent-child functionality
 * }
 * ```
 */
export const validateParentChildData = (item: ItemDetails, versions: IItemVersion[]): boolean => {
  if (!item || !versions) {
    return false;
  }

  // Check if all versions have required fields
  return versions.every((version) => version.taxonomyId && (version.name || version.displayText || version.taxonomyId));
};
